import { getSupabaseAnonKey, getSupabaseUrl } from "@/lib/supabaseEnv";
import { createBrowserClient } from "@supabase/ssr";
import type { SupabaseClient, User } from "@supabase/supabase-js";

let browserClient: SupabaseClient | null = null;
let currentUserRequest: Promise<User | null> | null = null;

function parseCookies(): Array<{ name: string; value: string }> {
  return document.cookie.split(";").map((c) => {
    const [name, ...rest] = c.trim().split("=");
    return { name, value: rest.join("=") };
  });
}

export function getSupabaseBrowserClient(): SupabaseClient {
  if (browserClient) {
    return browserClient;
  }

  browserClient = createBrowserClient(getSupabaseUrl(), getSupabaseAnonKey(), {
    cookies: {
      getAll() {
        return parseCookies();
      },
      setAll(cookies) {
        for (const { name, value, options } of cookies) {
          let cookie = `${name}=${value}`;
          if (options?.path) cookie += `; path=${options.path}`;
          if (options?.maxAge != null) cookie += `; max-age=${options.maxAge}`;
          if (options?.domain) cookie += `; domain=${options.domain}`;
          if (options?.sameSite) cookie += `; samesite=${options.sameSite}`;
          if (options?.secure) cookie += "; secure";
          document.cookie = cookie;
        }
      },
    },
  });
  return browserClient;
}

function isNavigatorLockRecoveryError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === "NavigatorLockAcquireTimeoutError" ||
    error.name === "AbortError" ||
    error.message.includes("Lock broken by another request") ||
    error.message.includes("was released because another request stole it")
  );
}

export async function getSupabaseCurrentUser(): Promise<User | null> {
  if (!currentUserRequest) {
    currentUserRequest = getSupabaseBrowserClient()
      .auth.getUser()
      .then(({ data, error }) => {
        if (error) {
          throw error;
        }
        return data.user;
      })
      .catch(async (error) => {
        if (!isNavigatorLockRecoveryError(error)) {
          throw error;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
        const { data, error: retryError } = await getSupabaseBrowserClient().auth.getUser();
        if (retryError) {
          throw retryError;
        }
        return data.user;
      })
      .finally(() => {
        currentUserRequest = null;
      });
  }

  return currentUserRequest;
}
